import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Radio } from 'antd'
import { isEmpty } from 'ramda'
import dayjs from 'dayjs'
import $t from '../../../i18n'
import Page from '../../common/Page/Page'
import Dialog from '../../common/Dialog'
import MaterialInput from '../../common/MaterialInput'
import Actions from '../../../actions'
import validateZipcode from '../../../utils/validateZipcode'
import Alert from '../../common/Alert'
import { event, eventTypes } from '../../../consts/eventConsts'
import {
  eliminateNewLineCharFromString,
  formatClientSideError,
} from '../../../utils/eventUtils'
import {
  getIsHmoMember,
  isMemberInsuranceActivated,
} from '../../../utils/hmoUtils'
import { getContactSupportUrl, routes } from '../../../consts'
import ActionButton from '../../common/ActionButton/ActionButton'

import './DeclareLocation.css'

const getValidationErrors = ({ address, city, zipcode }) => {
  const result = {}
  let cityLegalChars = new RegExp('^[a-zA-Z-\u0590-\u05FF ]+$')
  if (!address) {
    result.address = $t('errors.validation.missing')
  }
  if (!city) {
    result.city = $t('errors.validation.missing')
  } else if (!cityLegalChars.test(city)) {
    result.city = $t('errors.validation.invalidCity')
  }

  if (!zipcode) {
    result.zipcode = $t('errors.validation.missing')
  } else if (!validateZipcode(zipcode)) {
    result.zipcode = $t('errors.validation.invalidZipcode')
  }

  return (!isEmpty(result) && result) || null
}

const DeclareLocation = ({ isDialog, onNext }) => {
  const dispatch = useDispatch()

  const userDetails = useSelector(state => state.profile?.userDetails)
  const isHmoMember = getIsHmoMember(userDetails)
  // if is hmo with active status
  const isHmoActiveMember =
    isHmoMember && isMemberInsuranceActivated(userDetails)

  const { isDeclareLocationDialogVisible, shouldSkipLocationVerification } =
    useSelector(state => state.payment)
  const isNoServiceDisplay = useSelector(state => state.app?.isNoServiceDisplay)
  const validStates = useSelector(state => state.app?.validStates)

  // if last address is home address, we will reset another location fields
  const lastAddressIsHomeAddress = useSelector(
    state => state.account?.lastLocation?.is_home_address
  )
  const lastAddress = useSelector(
    state => state.account?.lastLocation?.address_street
  )
  const lastCity = useSelector(
    state => state.account?.lastLocation?.address_city
  )
  const lastZipcode = useSelector(
    state => state.account?.lastLocation?.address_zip
  )

  const [isHmoHomeAddressSelected, setIsHmoHomeAddressSelected] =
    useState(isHmoActiveMember)
  const homeAddress = userDetails?.address?.find(
    address => address?.address_type === 'Residential'
  )

  const fullHomeAddress = homeAddress
    ? [
        homeAddress?.address1,
        homeAddress?.address2,
        homeAddress?.city,
        homeAddress?.state + ' ' + homeAddress?.zipCode,
      ]
        .filter(item => item)
        .join(', ')
    : ''

  const timestamp = useSelector(state => state.account?.lastLocation?.timestamp)

  const states = validStates.join(', ')

  const isValidState = payload => {
    // on next comes from wizard component
    // we can use this component without the wizard and and 'onnext' will be undefined
    dispatch(
      Actions.createAction(Actions.VALIDATE_STATE_REQUESTED, {
        ...payload,
        onNext,
      })
    )
  }

  useEffect(() => {
    if (isDeclareLocationDialogVisible) {
      setShouldDisplayMedicareStep(false)
      setShouldDisplayMedicareAlert(false)
    }
  }, [isDeclareLocationDialogVisible])

  useEffect(() => {
    if (shouldSkipLocationVerification && isDialog) {
      const locationData = localStorage.getItem('locationData')
      if (locationData) {
        let payload = JSON.parse(locationData)

        isValidState(payload)
        dispatch(
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            eventName: 'is_medicate',
            data: { is_medicate: 'no' },
          })
        )
      }
    }
  }, [shouldSkipLocationVerification, isDialog])

  const [address, setAddress] = useState(
    lastAddressIsHomeAddress ? '' : lastAddress
  )
  const [city, setCity] = useState(lastAddressIsHomeAddress ? '' : lastCity)
  const [zipcode, setZipcode] = useState(
    lastAddressIsHomeAddress ? '' : lastZipcode
  )
  const [shouldValidate, setShouldValidate] = useState(false)
  const [shouldDisplayMedicareStep, setShouldDisplayMedicareStep] =
    useState(false)
  const [shouldDisplayMedicareAlert, setShouldDisplayMedicareAlert] =
    useState(false)
  const [
    shouldDisplayMedicareAreYouSureAlert,
    setShouldDisplayMedicareAreYouSureAlert,
  ] = useState(false)

  const errors =
    shouldValidate &&
    !isHmoHomeAddressSelected &&
    getValidationErrors({ zipcode, city, address })

  const onAbort = () => {
    dispatch(Actions.createAction(Actions.LOCATION_VERIFICATION_CLOSED))
    setShouldDisplayMedicareStep(false)
    setShouldDisplayMedicareAlert(false)
  }

  const onEndSession = () => {
    dispatch(Actions.createAction(Actions.NOT_SERVICE_POPUP_CLOSED))
    dispatch(Actions.createAction(Actions.SIGNOUT_REQUESTED))
    setShouldDisplayMedicareStep(false)
    setShouldDisplayMedicareAlert(false)
  }

  const onSubmitNoServiceHmo = () => {
    dispatch(Actions.createAction(Actions.NOT_SERVICE_POPUP_CLOSED))
    dispatch(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.ROOT))
    localStorage.removeItem('isLocationVerified')
    localStorage.removeItem('locationData')
    setShouldDisplayMedicareStep(false)
    setShouldDisplayMedicareAlert(false)
  }

  const shouldSkipMedicareStep = () => {
    const timeStamp = dayjs(timestamp)
    const today = dayjs()
    const difference = today.diff(timeStamp, 'd')
    return (
      (lastAddress && lastCity && lastZipcode && difference <= 7) || isHmoMember
    )
  }

  const onSubmitLocation = () => {
    const payload = { address, city, zipcode, state: validateZipcode(zipcode) }
    if (getValidationErrors(payload) && !isHmoHomeAddressSelected) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: payload,
          element: 'Location Verification',
          error: formatClientSideError(getValidationErrors(payload)),
        })
      )
      return
    }

    if (shouldSkipMedicareStep()) {
      onSubmit()
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.submit,
          data: payload,
          element: 'Location Verification',
          userScope: {
            zipcode,
            usa_state: validateZipcode(zipcode),
          },
        })
      )
    } else {
      setShouldDisplayMedicareStep(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.submit,
          data: payload,
          element: 'Location Verification',
          userScope: {
            zipcode,
            usa_state: validateZipcode(zipcode),
          },
        })
      )
    }
  }

  const onSubmit = () => {
    const payload = isHmoHomeAddressSelected
      ? {
          // in case address2 is null we don't want to get null in the address
          address: homeAddress?.address1 + ' ' + (homeAddress?.address2 || ''),
          city: homeAddress?.city,
          zipcode: homeAddress?.zipCode,
          state: homeAddress?.state,
          isHomeAddress: isHmoHomeAddressSelected,
        }
      : {
          address,
          city,
          zipcode,
          state: validateZipcode(zipcode),
          isHomeAddress: isHmoHomeAddressSelected,
        }
    localStorage.setItem('isLocationVerified', true)
    localStorage.setItem('locationData', JSON.stringify(payload))
    isValidState(payload)
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: {
          question_text: eliminateNewLineCharFromString(medicareStepTitle),
          answer_text: $t('common.no'),
        },
        element: 'Medicare/Medicaid',
      })
    )
  }

  const declareStepActions = [
    <Button
      key="countinuebtn"
      className="dialog-action"
      type="primary"
      onClick={onSubmitLocation}
    >
      {$t('common.continue')}
    </Button>,
  ]

  const medicareStepActions = [
    <Button
      key="nobtn"
      className="dialog-action"
      type="primary"
      onClick={onSubmit}
    >
      {$t('common.no')}
    </Button>,
    <Button
      key="yesbtn"
      className="dialog-action"
      type="secondary"
      onClick={() => {
        setShouldDisplayMedicareAreYouSureAlert(true)
        dispatch(
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            type: eventTypes.formEvent,
            eventName: event.formEvent.submit,
            data: {
              question_text: eliminateNewLineCharFromString(medicareStepTitle),
              answer_text: $t('common.yes'),
            },
            element: 'Medicare/Medicaid',
          })
        )
      }}
    >
      {$t('common.yes')}
    </Button>,
  ]

  const dialogActions = shouldDisplayMedicareStep ? [] : declareStepActions

  const noServiceDialogActions = [
    <Button
      className="dialog-action"
      type="primary"
      key="contactbtn"
      onClick={() => {
        window.open(getContactSupportUrl(isHmoMember))
        onEndSession()
      }}
    >
      {$t('serviceLocation.link')}
    </Button>,
    <Button
      key="endbtn"
      className="dialog-action"
      type="secondary"
      onClick={onEndSession}
    >
      {$t('serviceLocation.endSession')}
    </Button>,
  ]

  const noServiceHmoDialogActions = [
    <Button
      key="gotItbtn"
      className="dialog-action"
      type="primary"
      onClick={onSubmitNoServiceHmo}
    >
      {$t('common.gotIt')}
    </Button>,
  ]

  const medicareStepTitle = shouldDisplayMedicareAlert
    ? $t('dialogs.locationVerify.medicareOrMedicate.popupTitle')
    : $t('dialogs.locationVerify.medicareOrMedicate.title')

  const medicareStepSubTitle = shouldDisplayMedicareAlert
    ? $t('dialogs.locationVerify.medicareOrMedicate.popup')
    : $t('dialogs.locationVerify.medicareOrMedicate.text1')

  const dialogTitle =
    shouldDisplayMedicareStep || shouldDisplayMedicareAlert
      ? medicareStepTitle
      : $t('dialogs.locationVerify.title')

  const dialogSubTitle =
    shouldDisplayMedicareStep || shouldDisplayMedicareAlert
      ? medicareStepSubTitle
      : $t('dialogs.locationVerify.message')

  const renderContent = () => {
    return (
      <div className="dialog-content">
        <h2 className="subtitle">
          {isNoServiceDisplay
            ? isHmoMember
              ? $t('serviceLocation.noServiceHmo', { states })
              : $t('serviceLocation.message', { states })
            : dialogSubTitle}
        </h2>
        {shouldDisplayMedicareStep && !isNoServiceDisplay && (
          <div className="medicare-step">
            <div className="ant-modal-footer medicare-actions">
              {medicareStepActions}
            </div>
            <div className="medicare-note">
              <span className="bold">
                {' '}
                {$t('dialogs.locationVerify.medicareOrMedicate.noteTitle')}{' '}
              </span>{' '}
              {$t('dialogs.locationVerify.medicareOrMedicate.note1')}
            </div>
          </div>
        )}
        {!isNoServiceDisplay &&
          !shouldDisplayMedicareStep &&
          !shouldDisplayMedicareAlert &&
          (isHmoActiveMember
            ? getHMOAddressesOption()
            : getAddressInputFields())}
      </div>
    )
  }

  const getHMOAddressesOption = () => {
    return (
      <div className="hmo-addresses-options">
        <li className="list-item hmo-option-item">
          <ActionButton
            key={1}
            label={
              $t('dialogs.locationVerify.homeAddress') + ': ' + fullHomeAddress
            }
            iconName={isHmoHomeAddressSelected ? 'check' : 'check-circle-emply'}
            size={12}
            className={
              isHmoHomeAddressSelected
                ? 'hmo-addresses-option trackable checked'
                : 'hmo-addresses-option trackable'
            }
            onPress={() => setIsHmoHomeAddressSelected(true)}
          />
        </li>
        <li className="list-item hmo-option-item">
          <ActionButton
            key={2}
            label={$t('dialogs.locationVerify.anotherAddress')}
            iconName={
              !isHmoHomeAddressSelected ? 'check' : 'check-circle-emply'
            }
            size={12}
            className={
              !isHmoHomeAddressSelected
                ? 'hmo-addresses-option trackable checked'
                : 'hmo-addresses-option trackable'
            }
            onPress={() => setIsHmoHomeAddressSelected(false)}
          />
          {!isHmoHomeAddressSelected && getAddressInputFields()}
        </li>
      </div>
    )
  }

  const getAddressInputFields = () => {
    return (
      <div className="input-container">
        <MaterialInput
          className="address-input"
          value={address || ''}
          errorText={errors?.address}
          onChange={e => setAddress(e.target.value)}
          label={$t('dialogs.locationVerify.street')}
        />
        <MaterialInput
          className="city-input"
          value={city || ''}
          errorText={errors?.city}
          onChange={e => setCity(e.target.value)}
          label={$t('dialogs.locationVerify.city')}
        />
        <div className="row">
          <MaterialInput
            className="zip-input"
            type="number"
            value={zipcode || ''}
            errorText={errors?.zipcode}
            onChange={e =>
              e.target.value?.length <= 5 ? setZipcode(e.target.value) : {}
            }
            label={$t('dialogs.locationVerify.zip')}
            max={99999}
            min={0}
          />
          <MaterialInput
            containerClassName="state-input"
            value={validateZipcode(zipcode) || ''}
            disabled={true}
            label={$t('dialogs.locationVerify.state')}
          />
        </div>
      </div>
    )
  }

  const renderMedicateAlert = () => {
    return (
      <Alert
        title={$t('dialogs.locationVerify.medicareOrMedicate.alert')}
        shouldDisplay={shouldDisplayMedicareAreYouSureAlert}
        actions={[
          <Button
            className="dialog-action"
            type="primary"
            key="medicareno"
            onClick={() => {
              setShouldDisplayMedicareAreYouSureAlert(false)

              dispatch(
                Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                  eventName: 'Medicare_are_you_sure',
                  data: { is_medicate: 'no' },
                })
              )
            }}
          >
            {$t('common.no')}
          </Button>,
          <Button
            className="dialog-action"
            type="secondary"
            key="medicareyes"
            onClick={() => {
              setShouldDisplayMedicareAreYouSureAlert(false)
              setShouldDisplayMedicareAlert(true)
              setShouldDisplayMedicareStep(false)

              dispatch(
                Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                  eventName: 'Medicare_are_you_sure',
                  data: { is_medicate: 'yes' },
                })
              )
            }}
          >
            {$t('common.yes')}
          </Button>,
        ]}
        onOk={() => setShouldDisplayMedicareAreYouSureAlert(false)}
      />
    )
  }

  return isDialog ? (
    <Dialog
      title={isNoServiceDisplay ? $t('serviceLocation.title') : dialogTitle}
      isVisible={isDeclareLocationDialogVisible}
      footerActions={
        isNoServiceDisplay || shouldDisplayMedicareAlert
          ? noServiceDialogActions
          : dialogActions
      }
      onCancel={onAbort}
      closable={!isNoServiceDisplay}
      className="declare-location-dialog"
    >
      {renderContent()}
      {renderMedicateAlert()}
    </Dialog>
  ) : (
    <Page title={dialogTitle} className="declare-location-page">
      {renderContent()}
      {renderMedicateAlert()}
      <div className="page-footer">{dialogActions}</div>
      <Dialog
        title={$t('serviceLocation.title')}
        isVisible={isNoServiceDisplay || shouldDisplayMedicareAlert}
        footerActions={
          isHmoMember ? noServiceHmoDialogActions : noServiceDialogActions
        }
        closable={false}
        className="declare-location-dialog"
      >
        {renderContent()}
        {renderMedicateAlert()}
      </Dialog>
    </Page>
  )
}

export default DeclareLocation
